import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, Sparkles, ChevronRight, Share2, Check } from 'lucide-react';
import api from '../services/api';
import AnalysisReport from '../components/analysis/AnalysisReport';

export default function AnalysisView() {
  const { publicId } = useParams();
  const [searchParams] = useSearchParams();
  const fromCreate = searchParams.get('from') === 'create';

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await api.get(`/analysis/public/${publicId}`);
        setResult(response.data);
      } catch (err) {
        setError(err.response?.status === 404
          ? 'Analysis not found. The link may be invalid or expired.'
          : 'Failed to load analysis. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysis();
  }, [publicId]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href.split('?')[0]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => {
    window.location.href = '/analysis';
  };

  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-bastion-blue-50 text-bastion-black px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            {fromCreate ? 'Analysis Complete' : 'Handwriting Analysis Report'}
          </div>
          <h1 className="section-title">
            {fromCreate ? 'Your 12-Point Diagnosis Report' : 'Handwriting Analysis Report'}
          </h1>

          {/* Share link button */}
          <button
            onClick={handleCopyLink}
            className={`inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              copied
                ? 'bg-green-500 text-white'
                : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
            }`}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                Link copied!
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4" />
                Copy shareable link
              </>
            )}
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="card text-center py-16">
            <Loader2 className="w-10 h-10 animate-spin text-bastion-blue-500 mx-auto mb-4" />
            <p className="text-bastion-gray-600">Loading analysis...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="card text-center py-12">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <p className="text-red-700 mb-4">{error}</p>
            <Link to="/analysis" className="btn-primary inline-flex">
              Analyze Your Handwriting
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Report */}
        {result && (
          <AnalysisReport result={result} onReset={handleReset} />
        )}
      </div>
    </div>
  );
}
